import {
  ExpandableSection,
  ExpandableSectionProps,
  Flex,
  HelpFlexAnchor,
  Icon,
  smBumps,
  Typo,
  xsBumps,
} from '@wrisk/ui-components'
import React, { FunctionComponent } from 'react'

import { Quote } from '../../../domain'
import { useOptionalCovers } from '../../../hooks/covers'
import {
  tCovers,
  tFormats,
  useWriskTranslation,
} from '../../../infrastructure/internationalisation'
import {
  getCoreAssetType,
  getOptionalCoverLink,
  getOptionalCoversContentBaseUrl,
  useConfig,
} from '../../../state/configuration'
import { useProduct } from '../../product/productContext'

export interface OptionalCoversExpandableProps extends ExpandableSectionProps {
  quote: Quote
  showPremiums?: boolean
}

export const OptionalCoversExpandable: FunctionComponent<
  OptionalCoversExpandableProps
> = ({ quote, showPremiums, header, ...props }) => {
  const { t } = useWriskTranslation()
  const { scheme, product } = useProduct()

  const optionalCovers = useOptionalCovers(quote, product).filter((it) => it.active)
  const contentBaseUrl = useConfig(getOptionalCoversContentBaseUrl(scheme.schemeCode))

  const assetType = getCoreAssetType(product)

  const cells = optionalCovers.map((it) => {
    const insurer = it.insurer
    const link =
      insurer &&
      getOptionalCoverLink(contentBaseUrl, quote.wordingVersion, { ...it, insurer })

    const premium = showPremiums && (
      <Typo typoSize='sm'>
        {t(tFormats('currency.long'), {
          amount: it.premium,
        })}
      </Typo>
    )
    return (
      <HelpFlexAnchor
        key={`${assetType.code}.${it.coverGroup}`}
        href={link}
        width={1}
        variant='highlight'
        px={smBumps}
        py={3}
      >
        <Flex>
          <Icon icon='tick' size='small' mr={smBumps} />
          <Typo typoSize='sm'>{t(tCovers(it.coverGroup, 'name'))}</Typo>
        </Flex>
        <Flex>
          {premium}
          <Icon size='tiny' icon='info' ml={xsBumps} />
        </Flex>
      </HelpFlexAnchor>
    )
  })

  return cells.length ? (
    <ExpandableSection header={header} {...props}>
      {cells}
    </ExpandableSection>
  ) : null
}
